/**
 * Auth Zustand store — persists to localStorage, includes devtools.
 *
 * State: user, token, refreshToken, isAuthenticated
 * Actions: login, logout, setTokens, refreshAuth
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { User, LoginCredentials, LoginResponse } from "@/types";
import { assertCurrentSession, getSession, onSessionChange, startNewSession } from "@/services/sessionLifecycle";
import { useChatStore } from "./chatStore";
import { useNotificationStore } from "./notificationStore";
import { useToastStore } from "./toastStore";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

type RefreshSessionResponse = {
  accessToken: string;
  refreshToken?: string;
  user?: User;
  mustChangePassword?: boolean;
};

let refreshPromise: { session: ReturnType<typeof getSession>; promise: Promise<boolean> } | null = null;
let logoutPromise: Promise<void> = Promise.resolve();

onSessionChange(() => {
  if (refreshPromise?.session !== getSession()) refreshPromise = null;
  useChatStore.getState().clearChat();
  useNotificationStore.getState().clearNotifications();
  useToastStore.setState({ toasts: [] });
});

async function applyUserLanguage(user: User | null, session = getSession()) {
  if (!user?.language) return;
  const { default: i18n } = await import("@/i18n");
  assertCurrentSession(session);
  await i18n.changeLanguage(user.language);
  assertCurrentSession(session);
  localStorage.setItem("uklid-language", user.language);
}

function authHeaders(token: string | null) {
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

function isDifferentUserSession(current: User | null, next: User) {
  return current?.id !== next.id || current.role !== next.role || current.workerId !== next.workerId;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  /** Authenticate with username + password. Returns mustChangePassword flag. */
  login: (credentials: LoginCredentials) => Promise<{ mustChangePassword: boolean }>;
  /** Clear all auth state and call the server logout. */
  logout: () => void;
  /** Update tokens (used after automatic refresh). */
  setTokens: (token: string, refreshToken: string, user?: User) => void;
  /** Attempt to refresh the current session. */
  refreshAuth: () => Promise<boolean>;
  /** Fetch the current user for a persisted/cookie session. */
  refreshCurrentUser: () => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,

        login: async (credentials: LoginCredentials) => {
          const session = startNewSession(() => {
            set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
          });
          // Do not let a late logout response clear the next login's cookie.
          await logoutPromise;
          assertCurrentSession(session);
          // Direct fetch — avoid circular dependency on api.ts
          const resp = await fetch(
            `${BASE_URL}/api/auth/login`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(credentials),
              credentials: "include",
              signal: session.controller.signal,
            },
          );
          assertCurrentSession(session);

          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}));
            throw new Error(data.error ?? "Login failed");
          }

          const data: LoginResponse = await resp.json();
          assertCurrentSession(session);
          await applyUserLanguage(data.user, session);
          assertCurrentSession(session);

          // Refresh token comes as httpOnly cookie — we also store it
          // in memory for the 401-retry flow if the server returns it.
          // Discard any reads started while the login was pending as well.
          startNewSession(() => set({
            user: data.user,
            token: data.accessToken,
            refreshToken: data.refreshToken ?? null,
            isAuthenticated: true,
          }));

          // Return mustChangePassword flag to redirect if needed
          return { mustChangePassword: data.mustChangePassword ?? false };
        },

        logout: () => {
          startNewSession(() => set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          }));
          // Fire-and-forget server logout to clear cookies
          const request = fetch(`${BASE_URL}/api/auth/logout`, {
            method: "POST",
            credentials: "include",
            signal: AbortSignal.timeout(10_000),
          }).then(() => {}).catch(() => {});
          logoutPromise = Promise.all([logoutPromise, request]).then(() => {});
        },

        setTokens: (token, refreshToken, user) => {
          const changedIdentity = !get().isAuthenticated || (user && isDifferentUserSession(get().user, user));
          const publish = () => set((state) => ({
            token,
            refreshToken,
            user: user ?? state.user,
            isAuthenticated: true,
          }));
          const session = changedIdentity ? startNewSession(publish) : getSession();
          if (!changedIdentity) publish();
          if (user) void applyUserLanguage(user, session).catch(() => {});
        },

        refreshAuth: () => {
          const session = getSession();
          if (refreshPromise?.session === session) return refreshPromise.promise;
          const { refreshToken: rt } = get();
          const promise = (async () => {
            try {
              const resp = await fetch(
                `${BASE_URL}/api/auth/refresh`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: rt ? JSON.stringify({ refreshToken: rt }) : "{}",
                  credentials: "include",
                  signal: session.controller.signal,
                },
              );
              if (getSession() !== session) return false;

              if (!resp.ok) {
                get().logout();
                return false;
              }

              const data = (await resp.json()) as RefreshSessionResponse;
              if (getSession() !== session) return false;
              const user = data.user ?? get().user;
              const changedIdentity = user && isDifferentUserSession(get().user, user);
              const publish = () => set({
                token: data.accessToken,
                refreshToken: data.refreshToken ?? (changedIdentity ? null : rt),
                user,
                isAuthenticated: true,
              });
              const resolvedSession = changedIdentity ? startNewSession(publish) : session;
              if (!changedIdentity) publish();
              await applyUserLanguage(user, resolvedSession);
              if (getSession() !== resolvedSession) return false;
              return true;
            } catch {
              if (getSession() === session) get().logout();
              return false;
            } finally {
              if (refreshPromise?.session === session) refreshPromise = null;
            }
          })();
          refreshPromise = { session, promise };
          return promise;
        },

        refreshCurrentUser: async () => {
          const session = getSession();
          const fetchCurrentUser = (token: string | null) =>
            fetch(`${BASE_URL}/api/auth/me`, {
              method: "GET",
              headers: authHeaders(token),
              credentials: "include",
              signal: session.controller.signal,
            });

          try {
            let resp = await fetchCurrentUser(get().token);
            if (getSession() !== session) return false;
            if (resp.status === 401) {
              const refreshed = await get().refreshAuth();
              if (!refreshed) return false;
              if (getSession() !== session) return false;
              resp = await fetchCurrentUser(get().token);
            }
            if (getSession() !== session) return false;

            if (!resp.ok) {
              get().logout();
              return false;
            }

            const user = (await resp.json()) as User;
            if (getSession() !== session) return false;
            if (!user?.role) {
              get().logout();
              return false;
            }

            const changedIdentity = isDifferentUserSession(get().user, user);
            const publish = () => set({ user, isAuthenticated: true });
            const resolvedSession = changedIdentity ? startNewSession(publish) : session;
            if (!changedIdentity) publish();
            await applyUserLanguage(user, resolvedSession);
            if (getSession() !== resolvedSession) return false;
            return true;
          } catch {
            if (getSession() === session) get().logout();
            return false;
          }
        },
      }),
      {
        name: "uklid-auth",
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
    { name: "AuthStore" },
  ),
);
